import React, { useState, useEffect, useCallback } from "react";
import { FaArrowRight } from "react-icons/fa";

import ACHIEVEMENT_PODIUM from "../../assets/photos/achievement-podium.jpg";
import ACHIEVEMENT_GROUP from "../../assets/photos/achievement-group.jpg";
import ACHIEVEMENT_MEDALS from "../../assets/photos/achievement-medals.jpg";
import ACHIEVEMENT_BOYS from "../../assets/photos/achievement-boys.jpg";
import HOLI from "../../assets/photos/holi-celebration.jpg";
import TRAINING_SESSION from "../../assets/photos/training-session.jpg";
import TEAM_TROPHY from "../../assets/photos/team-trophy.jpg";
import SCHOOL_COMP from "../../assets/photos/school-competition.jpg";
import CHRISTMAS from "../../assets/photos/christmas-fun.jpg";
import COACH_KIDS from "../../assets/photos/coach-with-kids.jpg";
import EVENT from "../../assets/photos/event-inauguration.jpg";
import HERO from "../../assets/photos/hero-training.jpg";
import PRO_COACH from "../../assets/photos/coach-handstand-training.jpg";
import PRO_SCORPION from "../../assets/photos/kid-scorpion-pose.jpg";
import PRO_PLANCHE from "../../assets/photos/kid-planche-strength.jpg";
import PRO_GROUP from "../../assets/photos/group-bridge-training.jpg";
import PRO_SPLITS from "../../assets/photos/kids-splits-training.jpg";
import TEAM_BEAM_POSE from "../../assets/photos/team-beam-pose.jpg";
import TEAM_BEAM_DYN from "../../assets/photos/team-beam-dynamic.jpg";
import SOLO_LEG from "../../assets/photos/solo-leg-hold.jpg";
import BEAM_ARAB from "../../assets/photos/beam-arabesque.jpg";

const allImages = [
  { img: PRO_SPLITS, label: "Kids Splits Training", category: "training", span: "md:col-span-2 md:row-span-2" },
  { img: PRO_GROUP, label: "Group Bridge Training", category: "training", span: "md:col-span-2" },
  { img: PRO_COACH, label: "Coach Handstand Training", category: "training", span: "" },
  { img: PRO_SCORPION, label: "Flexibility — Scorpion Pose", category: "training", span: "" },
  { img: PRO_PLANCHE, label: "Strength & Balance", category: "training", span: "md:col-span-2" },
  { img: TEAM_BEAM_DYN, label: "Team on Balance Beam", category: "training", span: "md:col-span-2" },
  { img: TEAM_BEAM_POSE, label: "Team Beam Pose", category: "training", span: "" },
  { img: SOLO_LEG, label: "Standing Leg Hold", category: "training", span: "" },
  { img: BEAM_ARAB, label: "Beam Arabesque", category: "training", span: "" },
  { img: ACHIEVEMENT_PODIUM, label: "District Championship", category: "achievements", span: "md:col-span-2 md:row-span-2" },
  { img: ACHIEVEMENT_MEDALS, label: "Medal Winners", category: "achievements", span: "" },
  { img: ACHIEVEMENT_BOYS, label: "Golden Grip Tournament", category: "achievements", span: "" },
  { img: ACHIEVEMENT_GROUP, label: "State Competition", category: "achievements", span: "md:col-span-2" },
  { img: SCHOOL_COMP, label: "VIBGYOR School Competition", category: "achievements", span: "" },
  { img: TEAM_TROPHY, label: "Team Trophy", category: "achievements", span: "" },
  { img: HERO, label: "Training in Action", category: "training", span: "" },
  { img: COACH_KIDS, label: "Coach with Students", category: "training", span: "" },
  { img: TRAINING_SESSION, label: "Practice Session", category: "training", span: "md:col-span-2" },
  { img: CHRISTMAS, label: "Christmas Celebration", category: "events", span: "md:col-span-2" },
  { img: HOLI, label: "Holi with Students", category: "events", span: "md:col-span-2" },
  { img: EVENT, label: "Academy Inauguration", category: "events", span: "" },
];

const categories = [
  { key: "all", label: "All" },
  { key: "training", label: "Training" },
  { key: "achievements", label: "Achievements" },
  { key: "events", label: "Events & Celebrations" },
];

const Gallery = () => {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const filtered = filter === "all" ? allImages : allImages.filter((i) => i.category === filter);

  const openLightbox = (i) => setLightbox({ open: true, index: i });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const prevImage = useCallback(
    () => setLightbox((s) => ({ ...s, index: (s.index - 1 + filtered.length) % filtered.length })),
    [filtered.length]
  );
  const nextImage = useCallback(
    () => setLightbox((s) => ({ ...s, index: (s.index + 1) % filtered.length })),
    [filtered.length]
  );

  useEffect(() => {
    if (!lightbox.open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "ArrowRight") nextImage();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [lightbox.open, prevImage, nextImage]);

  // Reset lightbox when filter changes
  useEffect(() => {
    setLightbox({ open: false, index: 0 });
  }, [filter]);

  return (
    <section className="pt-24 pb-16 bg-slate-950 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-blue-400 font-semibold text-sm uppercase tracking-wider mb-3">
            Gallery
          </p>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4">
            Life at FlipCity
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            Training sessions, competitions, celebrations — moments that define
            our journey of building champions.
          </p>
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-8 md:gap-12 mb-12">
          {[
            { num: "25+", label: "Years" },
            { num: "100+", label: "Students" },
            { num: "50+", label: "Medals" },
            { num: `${allImages.length}`, label: "Photos" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-black text-white">{s.num}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setFilter(cat.key)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                filter === cat.key
                  ? "bg-primary-600 text-white shadow-lg shadow-primary-600/25"
                  : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            >
              {cat.label}
              <span className="ml-2 text-xs opacity-60">
                ({cat.key === "all" ? allImages.length : allImages.filter((i) => i.category === cat.key).length})
              </span>
            </button>
          ))}
        </div>

        {/* Masonry grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3">
          {filtered.map((item, i) => (
            <div
              key={`${filter}-${i}`}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer ${item.span}`}
              onClick={() => openLightbox(i)}
            >
              <img
                src={item.img}
                alt={item.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 md:translate-y-4 md:group-hover:translate-y-0 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300">
                <p className="text-white text-xs md:text-sm font-semibold drop-shadow-lg">{item.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-500 text-sm mb-4">
            Want to be part of the next photo?
          </p>
          <a
            href="https://wa.me/919582691655?text=Hi%2C%20I%27d%20like%20to%20book%20a%20free%20trial%20class"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-white text-slate-900 font-bold px-7 py-4 rounded-full hover:bg-blue-50 transition-all"
          >
            Book a Free Trial
            <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && filtered[lightbox.index] && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-5 right-5 text-white/70 hover:text-white text-3xl z-10 w-10 h-10 flex items-center justify-center"
            aria-label="Close lightbox"
          >
            ✕
          </button>
          <div className="absolute top-5 left-5 text-white/50 text-sm font-medium">
            {lightbox.index + 1} / {filtered.length}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 md:left-8 text-white/50 hover:text-white text-4xl z-10 w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors"
            aria-label="Previous image"
          >
            ‹
          </button>
          <div
            className="max-w-5xl max-h-[85vh] px-4 md:px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={filtered[lightbox.index].img}
              alt={filtered[lightbox.index].label}
              className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg"
            />
            <p className="text-center text-white/70 text-sm mt-4 font-medium">
              {filtered[lightbox.index].label}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 md:right-8 text-white/50 hover:text-white text-4xl z-10 w-12 h-12 flex items-center justify-center hover:bg-white/10 rounded-full transition-colors"
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
